import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  Search,
  Download,
  FileDown,
  Plus,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  CalendarPlus,
  AlertCircle,
} from 'lucide-react'

// New bookings must go through the validated funnel (availability → price → payment → QR → email);
// the reservation model is read-only by design, so "Nuova prenotazione" launches the booking app.
const BOOKING_APP_URL = 'https://app.garagesanmarco.it/it'
import { format, parseISO, differenceInCalendarDays } from 'date-fns'
import { getContentList, exportListUrl, type ContentEntity } from '../../lib/api/content'
import { ReservationDrawer } from './ReservationDrawer'
import { cn } from '../../lib/cn'
import { getReservationEmailStatuses } from '../../lib/api/client'

/* confirmation-email status dot for the list */
function EmailDot({ status }: { status?: string }) {
  const s = (status || '').toUpperCase()
  const color =
    s === 'SENT' ? 'text-ok' : s === 'FAILED' ? 'text-venetian' : s === 'PENDING' ? 'text-oro-700' : 'text-faint'
  const title =
    s === 'SENT'
      ? 'Email inviata'
      : s === 'FAILED'
        ? 'Email non inviata'
        : s === 'PENDING'
          ? 'Email in coda'
          : 'Nessuna email'
  return <span title={title} className={cn('inline-block h-2 w-2 shrink-0 rounded-full bg-current', color)} />
}

const PAGE_SIZE = 50

const STATUS_SEG = [
  { value: '', label: 'Tutte' },
  { value: 'CONFIRMED', label: 'Confermate' },
  { value: 'DRAFT', label: 'Bozze' },
  { value: 'CANCELLED', label: 'Annullate' },
]

const SEARCH_FIELDS = ['code', 'name', 'lastName', 'licencePlate', 'email', 'sourceReference']

const emptyFilters = { status: '', transport: '', paymentMode: '', fromDate: '', toDate: '' }
// opens on "Tutte" — all reservations, no status filter
const defaultFilters = { ...emptyFilters }

/* ── channel badge — neutral chip, the label is the differentiator (no gold/blue/violet) ── */
function channelBadge(src: string) {
  const s = (src || '').toUpperCase()
  return { cls: 'text-slate border-line bg-paper', label: s || 'DIRETTO' }
}
function statusChip(status: string) {
  const s = (status || '').toUpperCase()
  if (s === 'CANCELLED' || s === 'CANCELED') return { cls: 'bg-venetian-soft text-venetian', label: 'Annullata' }
  if (s === 'DRAFT') return { cls: 'bg-oro-soft text-oro-700', label: 'Bozza' }
  return { cls: 'bg-ok-soft text-ok', label: 'Confermata' }
}
function toDate(v: unknown): Date | null {
  if (typeof v === 'number') return new Date(v < 1e12 ? v * 1000 : v) // Unix seconds or ms
  if (typeof v === 'string' && v) {
    try {
      const d = parseISO(v)
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  return null
}
function fmtDateTime(v: unknown): string {
  const d = toDate(v)
  // include the 2-digit year (e.g. 04/06/26) so the year is always visible
  return d ? format(d, 'dd/MM/yy HH:mm') : typeof v === 'string' ? v : '—'
}
// date + time on two lines, for the compact "Soggiorno" / "Prenotato" columns
function dateTimeParts(v: unknown): { date: string; time: string } {
  const d = toDate(v)
  return d ? { date: format(d, 'dd/MM/yy'), time: format(d, 'HH:mm') } : { date: '—', time: '' }
}
function nightsBetween(a: unknown, l: unknown): number {
  const da = toDate(a)
  const dl = toDate(l)
  if (!da || !dl) return 0
  return Math.max(0, differenceInCalendarDays(dl, da))
}
function money(c: unknown): string {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}
function ref(o: unknown): string {
  if (o == null || typeof o !== 'object') return ''
  const r = o as { elements?: Record<string, unknown> }
  return String((r.elements ?? {}).label ?? (r.elements ?? {}).name ?? '')
}

// column header helper (sortable)
function Th({
  children,
  field,
  sort,
  onSort,
  className,
  right,
}: {
  children: React.ReactNode
  field?: string
  sort?: { field: string; desc: boolean }
  onSort?: (f: string) => void
  className?: string
  right?: boolean
}) {
  return (
    <th
      className={cn(
        'whitespace-nowrap px-4 py-[11px] text-[11px] font-bold uppercase tracking-[1.4px] text-muted',
        right ? 'text-right' : 'text-left',
        className,
      )}
    >
      {field && onSort ? (
        <button onClick={() => onSort(field)} className="inline-flex items-center gap-1 hover:text-coal">
          {children}
          {sort?.field === field ? (
            sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
          ) : (
            <ChevronsUpDown className="h-3 w-3 opacity-50" />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  )
}

// Compact card used on narrow widths (mobile / small tablet) in place of a table row.
function ReservationCard({
  row,
  checked,
  onToggle,
  onOpen,
}: {
  row: ContentEntity
  checked: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const el = row.elements as Record<string, unknown>
  const ch = channelBadge(String(el.source ?? ''))
  const st = statusChip(String(el.status ?? ''))
  return (
    <div
      onClick={onOpen}
      className={cn('cv-panel cursor-pointer p-3.5 transition-colors hover:bg-paper', checked && 'bg-verdigris-soft/40')}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5 accent-verdigris"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-semibold text-coal">
                {String(el.name ?? '')} {String(el.lastName ?? '')}
              </div>
              <div className="truncate text-[12px] text-muted">{String(el.email ?? '')}</div>
            </div>
            <span className={`gv-chip shrink-0 ${st.cls}`}>
              <i className="h-1.5 w-1.5 rounded-full bg-current" />
              {st.label}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
            {el.code ? <span className="font-mono text-muted">#{String(el.code)}</span> : null}
            <span className={`gv-src ${ch.cls}`}>{ch.label}</span>
            {el.licencePlate ? (
              <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-coal">
                {String(el.licencePlate)}
              </span>
            ) : null}
            {ref(el.transport) ? <span className="text-slate">{ref(el.transport)}</span> : null}
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-line-soft pt-2.5">
            <span className="font-mono text-[12px] text-slate">
              {fmtDateTime(el.arrivalDate)} → {fmtDateTime(el.leaveDate)}
            </span>
            <span className="font-mono text-[13px] font-semibold text-coal">{money(el.totalPrice)}</span>
          </div>
          <div className="mt-1 font-mono text-[11px] text-faint">Prenotato il {fmtDateTime(row.creationDate)}</div>
        </div>
      </div>
    </div>
  )
}

// Pagination footer — shared by the table and the card list.
function Pager({
  page,
  maxPage,
  total,
  from,
  to,
  rowsInPage,
  searching,
  busy,
  onPrev,
  onNext,
}: {
  page: number
  maxPage: number
  total: number
  from: number
  to: number
  rowsInPage: number
  searching: boolean
  busy: boolean
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 text-[12.5px] text-muted">
      <span className="tnum">
        {searching ? `${rowsInPage} in pagina · ` : ''}
        {from.toLocaleString('it-IT')}–{to.toLocaleString('it-IT')} di {total.toLocaleString('it-IT')}
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          className="rounded-md border border-line bg-card px-2 py-1 text-slate hover:border-faint disabled:opacity-40"
          onClick={onPrev}
          disabled={page === 0 || busy}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-1.5 tnum">
          {page + 1} / {maxPage + 1}
        </span>
        <button
          className="rounded-md border border-line bg-card px-2 py-1 text-slate hover:border-faint disabled:opacity-40"
          onClick={onNext}
          disabled={page >= maxPage || busy}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function ReservationsPage() {
  const [filters, setFilters] = useState(defaultFilters)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ field: string; desc: boolean }>({ field: 'creationDate', desc: true })
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<ContentEntity | null>(null)

  function patchFilter(p: Partial<typeof filters>) {
    setFilters((f) => ({ ...f, ...p }))
    setPage(0)
  }

  const filterExpr = useMemo(() => {
    const c: string[] = []
    if (filters.status) c.push(`status = '${filters.status}'`)
    if (filters.transport) c.push(`transport = '${filters.transport}'`)
    if (filters.paymentMode) c.push(`paymentMode = '${filters.paymentMode}'`)
    if (filters.fromDate) c.push(`arrivalDate >= '${filters.fromDate}'`)
    if (filters.toDate) c.push(`arrivalDate <= '${filters.toDate}'`)
    return c.join(' and ')
  }, [filters])
  const sortExpr = `${sort.field} ${sort.desc ? 'desc' : 'asc'}`

  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })
  const paymentModes = useQuery({
    queryKey: ['opt', 'paymentMode'],
    queryFn: () => getContentList('paymentMode', { limit: 50 }),
    staleTime: Infinity,
  })
  const opt = (r: ContentEntity) => ({
    value: r.id,
    label: String((r.elements.label as string) ?? (r.elements.name as string) ?? r.id),
  })

  const list = useQuery({
    queryKey: ['reservations', filterExpr, sortExpr, page],
    queryFn: () =>
      getContentList('reservation', {
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        filter: filterExpr || undefined,
        sort: sortExpr,
      }),
    placeholderData: keepPreviousData,
  })

  const allRows = list.data?.data ?? []
  const total = list.data?.totalCount ?? 0
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allRows
    return allRows.filter((r) => SEARCH_FIELDS.some((f) => String(r.elements[f] ?? '').toLowerCase().includes(q)))
  }, [allRows, search])

  // latest email status per visible reservation — one bulk call powers the list dot
  const emailCodes = useMemo(
    () => rows.map((r) => String((r.elements as Record<string, unknown>).code ?? '')).filter(Boolean),
    [rows],
  )
  const emailStatuses = useQuery({
    queryKey: ['emailStatuses', emailCodes],
    queryFn: () => getReservationEmailStatuses(emailCodes),
    enabled: emailCodes.length > 0,
    placeholderData: keepPreviousData,
  })
  const emailStatusMap = emailStatuses.data ?? {}

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min((page + 1) * PAGE_SIZE, total)
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)
  const hasFilters = filterExpr !== '' || search !== ''

  function toggleSort(field: string) {
    setSort((s) => (s.field === field ? { field, desc: !s.desc } : { field, desc: false }))
    setPage(0)
  }
  function toggleRow(id: string) {
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  const pageIds = rows.map((r) => r.id)
  const allChecked = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  function toggleAll() {
    setSelected((s) => {
      const n = new Set(s)
      if (allChecked) pageIds.forEach((id) => n.delete(id))
      else pageIds.forEach((id) => n.add(id))
      return n
    })
  }
  function exportAll() {
    window.open(exportListUrl('reservation', { filter: filterExpr || undefined, sort: sortExpr }), '_blank')
  }
  function exportSelected() {
    if (selected.size === 0) return
    const ids = [...selected].map((id) => `'${id}'`).join(', ')
    window.open(exportListUrl('reservation', { filter: `id in (${ids})` }), '_blank')
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
              Prenotazioni
            </h2>
            <span className="rounded-full bg-paper px-3 py-1 text-[12.5px] font-semibold tnum text-muted ring-1 ring-line">
              {total.toLocaleString('it-IT')} prenotazioni
            </span>
          </div>
          <p className="mt-1.5 text-[13.5px] text-muted">
            Tutte le prenotazioni da sito, Parkimeter, Parclick e ParkCloud — ricerca, filtri e azioni rapide.
          </p>
        </div>
        <div className="flex w-full items-center gap-2.5 sm:ml-auto sm:w-auto">
          {selected.size > 0 && (
            <button
              onClick={exportSelected}
              className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
            >
              <FileDown className="h-4 w-4" /> Selezionati ({selected.size})
            </button>
          )}
          <button
            onClick={exportAll}
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
          >
            <Download className="h-4 w-4" /> Esporta Excel
          </button>
          <a
            href={BOOKING_APP_URL}
            target="_blank"
            rel="noreferrer"
            title="Crea una prenotazione tramite il flusso di prenotazione (disponibilità, prezzo, pagamento, QR)"
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white shadow-[0_4px_14px_-6px_rgba(225,2,27,.55)] hover:bg-verdigris-700"
          >
            <Plus className="h-4 w-4" /> Nuova prenotazione <ExternalLink className="h-3.5 w-3.5 opacity-70" />
          </a>
        </div>
      </div>

      {/* filters */}
      <div className="mb-[18px] flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
          <input
            className="w-full rounded-lg border border-line bg-card py-[9px] pl-9 pr-3 text-[13.5px] text-slate outline-none placeholder:text-faint focus:border-verdigris"
            placeholder="Cerca per nome, targa, n° prenotazione…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* segmented status control */}
        <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
          {STATUS_SEG.map((s) => (
            <button
              key={s.label}
              onClick={() => patchFilter({ status: s.value })}
              className={cn(
                'px-3.5 py-2 text-[13px] font-semibold transition-colors',
                filters.status === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <select
          className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
          value={filters.transport}
          onChange={(e) => patchFilter({ transport: e.target.value })}
        >
          <option value="">Veicolo: tutti</option>
          {(transports.data?.data ?? []).map(opt).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
          value={filters.paymentMode}
          onChange={(e) => patchFilter({ paymentMode: e.target.value })}
        >
          <option value="">Pagamento: tutti</option>
          {(paymentModes.data?.data ?? []).map(opt).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
          <input
            type="date"
            className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
            title="Arrivo da"
            value={filters.fromDate}
            onChange={(e) => patchFilter({ fromDate: e.target.value })}
          />
          <span className="text-faint">→</span>
          <input
            type="date"
            className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
            title="Arrivo a"
            value={filters.toDate}
            onChange={(e) => patchFilter({ toDate: e.target.value })}
          />
        </div>
        {hasFilters && (
          <button
            onClick={() => {
              setFilters(defaultFilters)
              setSearch('')
              setPage(0)
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted hover:bg-card hover:text-coal"
          >
            <X className="h-4 w-4" /> Azzera
          </button>
        )}
      </div>

      {/* list — container-query responsive: cards when narrow, table when wide */}
      <div className="rsv-cq">
        {/* narrow (mobile / small tablet): card list */}
        <div className="rsv-cards space-y-2.5">
          {list.isError ? (
            <div className="cv-panel px-4 py-12 text-center text-sm text-muted">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le prenotazioni.
            </div>
          ) : list.isLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cv-panel p-3.5">
                <div className="skeleton h-[72px] w-full rounded bg-line-soft" />
              </div>
            ))
          ) : rows.length === 0 ? (
            <div className="cv-panel px-4 py-12 text-center text-sm text-muted">Nessuna prenotazione trovata.</div>
          ) : (
            <>
              {rows.map((row) => (
                <ReservationCard
                  key={row.id}
                  row={row}
                  checked={selected.has(row.id)}
                  onToggle={() => toggleRow(row.id)}
                  onOpen={() => setDetail(row)}
                />
              ))}
              <div className="cv-panel">
                <Pager
                  page={page}
                  maxPage={maxPage}
                  total={total}
                  from={from}
                  to={to}
                  rowsInPage={rows.length}
                  searching={!!search}
                  busy={list.isFetching}
                  onPrev={() => setPage((p) => Math.max(0, p - 1))}
                  onNext={() => setPage((p) => Math.min(maxPage, p + 1))}
                />
              </div>
            </>
          )}
        </div>

        {/* wide (tablet+ / laptop / desktop): table */}
        <div className="rsv-table-wrap cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line bg-card-2">
                  <th className="w-9 px-4 py-[11px]">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-verdigris" />
                  </th>
                  <Th field="code" className="rsv-code">
                    #
                  </Th>
                  <Th>Cliente</Th>
                  <Th field="arrivalDate" sort={sort} onSort={toggleSort} className="rsv-arrival">
                    Soggiorno
                  </Th>
                  <Th field="creationDate" sort={sort} onSort={toggleSort} className="rsv-leave">
                    Prenotato
                  </Th>
                  <Th field="source" sort={sort} onSort={toggleSort} className="rsv-mid">
                    Canale
                  </Th>
                  <Th className="rsv-plate">Targa</Th>
                  <Th field="transport" sort={sort} onSort={toggleSort} className="rsv-mid">
                    Veicolo
                  </Th>
                  <Th field="status" sort={sort} onSort={toggleSort}>
                    Stato
                  </Th>
                  <Th right>Totale</Th>
                  <th className="px-4 py-[11px]" />
                </tr>
              </thead>
              <tbody>
                {list.isError ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-14 text-center text-sm text-muted">
                      <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le prenotazioni.
                    </td>
                  </tr>
                ) : list.isLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft">
                      <td colSpan={11} className="px-4 py-[13px]">
                        <div className="skeleton h-4 w-full rounded bg-line-soft" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-14 text-center text-sm text-muted">
                      Nessuna prenotazione trovata.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const el = row.elements as Record<string, unknown>
                    const ch = channelBadge(String(el.source ?? ''))
                    const st = statusChip(String(el.status ?? ''))
                    const arr = dateTimeParts(el.arrivalDate)
                    const lea = dateTimeParts(el.leaveDate)
                    const booked = dateTimeParts(row.creationDate)
                    const nn = nightsBetween(el.arrivalDate, el.leaveDate)
                    return (
                      <tr
                        key={row.id}
                        onClick={() => setDetail(row)}
                        className={cn(
                          'cursor-pointer border-b border-line-soft last:border-0 hover:bg-paper',
                          selected.has(row.id) && 'bg-verdigris-soft/40',
                        )}
                      >
                        <td className="px-4 py-[13px]" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(row.id)}
                            onChange={() => toggleRow(row.id)}
                            className="accent-verdigris"
                          />
                        </td>
                        <td className="rsv-code whitespace-nowrap px-4 py-[13px] font-mono text-[12.5px] text-muted">
                          <span className="inline-flex items-center gap-2">
                            <EmailDot status={emailStatusMap[String(el.code ?? '')]} />
                            {String(el.code ?? '')}
                          </span>
                        </td>
                        <td className="px-4 py-[13px]">
                          <div className="font-semibold text-coal">
                            {String(el.name ?? '')} {String(el.lastName ?? '')}
                          </div>
                          <div className="mt-0.5 truncate text-[12px] text-muted">{String(el.email ?? '')}</div>
                        </td>
                        <td className="rsv-arrival whitespace-nowrap px-4 py-[11px]">
                          <div className="flex items-center gap-2">
                            <div className="font-mono text-[12px] leading-tight">
                              <div className="font-semibold text-coal">
                                {arr.date} <span className="font-normal text-muted">{arr.time}</span>
                              </div>
                              <div className="text-slate">
                                {lea.date} <span className="text-faint">{lea.time}</span>
                              </div>
                            </div>
                            {nn > 0 && (
                              <span className="rounded bg-paper px-1.5 py-0.5 text-[10.5px] font-bold text-muted ring-1 ring-line">
                                {nn}n
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="rsv-leave whitespace-nowrap px-4 py-[11px]">
                          <div className="flex items-center gap-1.5">
                            <CalendarPlus className="h-3.5 w-3.5 shrink-0 text-faint" />
                            <div className="font-mono text-[12px] leading-tight">
                              <div className="text-slate">{booked.date}</div>
                              {booked.time && <div className="text-faint">{booked.time}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="rsv-mid px-4 py-[13px]">
                          <span className={`gv-src ${ch.cls}`}>{ch.label}</span>
                        </td>
                        <td className="rsv-plate px-4 py-[13px]">
                          {el.licencePlate ? (
                            <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[12px] font-semibold tracking-wider text-coal">
                              {String(el.licencePlate)}
                            </span>
                          ) : (
                            <span className="text-faint">—</span>
                          )}
                        </td>
                        <td className="rsv-mid whitespace-nowrap px-4 py-[13px] text-slate">
                          {ref(el.transport) || '—'}
                        </td>
                        <td className="px-4 py-[13px]">
                          <span className={`gv-chip ${st.cls}`}>
                            <i className="h-1.5 w-1.5 rounded-full bg-current" />
                            {st.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-[13px] text-right font-mono text-[13px] font-semibold text-coal">
                          {money(el.totalPrice)}
                        </td>
                        <td className="px-4 py-[13px] text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setDetail(row)
                            }}
                            className="rounded-md border border-line bg-card px-2.5 py-1 text-[12px] font-semibold text-slate hover:border-faint"
                          >
                            Apri
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* footer */}
          <div className="border-t border-line-soft">
            <Pager
              page={page}
              maxPage={maxPage}
              total={total}
              from={from}
              to={to}
              rowsInPage={rows.length}
              searching={!!search}
              busy={list.isFetching}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => Math.min(maxPage, p + 1))}
            />
          </div>
        </div>
      </div>

      <ReservationDrawer row={detail} onClose={() => setDetail(null)} />
    </div>
  )
}
